import React, { FC } from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  IEmsAuditEvent,
  IEmsClaimSnapshot,
  IEmsEntitlementSnapshot,
  IEntitlementDeletedAuditEvent,
  IEntitlementUpsertedAuditEvent,
} from '../types/audit-event';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  const hours = String(date.getUTCHours()).padStart(2, '0');
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes} UTC`;
};

// --- Diff helpers ---

type ClaimDiffStatus = 'added' | 'removed' | 'modified' | 'unchanged';

export interface ClaimDiffRow {
  key: string;
  old: IEmsClaimSnapshot | null;
  new: IEmsClaimSnapshot | null;
  status: ClaimDiffStatus;
}

export const ROW_COLORS: Record<ClaimDiffStatus, string> = {
  added: 'success.50',
  removed: 'error.50',
  modified: 'warning.50',
  unchanged: 'transparent',
};

export function diffClaims(
  oldClaims: IEmsClaimSnapshot[] | null,
  newClaims: IEmsClaimSnapshot[] | null
): ClaimDiffRow[] {
  const oldList = oldClaims ?? [];
  const newList = newClaims ?? [];
  const oldMap = new Map(oldList.map((c) => [`${c.brand}|${c.code}`, c]));
  const newMap = new Map(newList.map((c) => [`${c.brand}|${c.code}`, c]));
  const allKeys = new Set([...Array.from(oldMap.keys()), ...Array.from(newMap.keys())]);

  return Array.from(allKeys).map((key) => {
    const oldClaim = oldMap.get(key) ?? null;
    const newClaim = newMap.get(key) ?? null;
    let status: ClaimDiffStatus;
    if (!oldClaim) status = 'added';
    else if (!newClaim) status = 'removed';
    else if (JSON.stringify(oldClaim) === JSON.stringify(newClaim)) status = 'unchanged';
    else status = 'modified';
    return { key, old: oldClaim, new: newClaim, status };
  });
}

export function diffRoles(
  oldRoles: string[] | null,
  newRoles: string[] | null
): { role: string; status: 'added' | 'removed' | 'unchanged' }[] {
  const oldSet = new Set(oldRoles ?? []);
  const newSet = new Set(newRoles ?? []);
  const all = new Set([...Array.from(oldSet), ...Array.from(newSet)]);
  return Array.from(all).map((role) => ({
    role,
    status: !oldSet.has(role) ? 'added' : !newSet.has(role) ? 'removed' : 'unchanged',
  }));
}

// --- Sub-components ---

export const DiffCell: FC<{
  oldVal: string | null | undefined;
  newVal: string | null | undefined;
  status: ClaimDiffStatus;
}> = ({ oldVal, newVal, status }) => {
  const old = oldVal ?? '-';
  const next = newVal ?? '-';
  if (status === 'added') return <>{next}</>;
  if (status === 'removed') return <>{old}</>;
  if (status === 'unchanged' || old === next) return <>{old}</>;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
      <Typography
        variant="inherit"
        sx={{ textDecoration: 'line-through', color: 'error.main', whiteSpace: 'nowrap' }}>
        {old}
      </Typography>
      <Typography variant="inherit" sx={{ color: 'text.disabled' }}>
        →
      </Typography>
      <Typography variant="inherit" sx={{ color: 'success.main', whiteSpace: 'nowrap' }}>
        {next}
      </Typography>
    </Stack>
  );
};

export const ClaimsDiffTable: FC<{ rows: ClaimDiffRow[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No claims
      </Typography>
    );
  }
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: 'grey.50' }}>
            <TableCell width={28} padding="checkbox" />
            <TableCell>Brand</TableCell>
            <TableCell>Code</TableCell>
            <TableCell>Active</TableCell>
            <TableCell>Valid from</TableCell>
            <TableCell>Valid to</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key} sx={{ backgroundColor: ROW_COLORS[row.status] }}>
              <TableCell padding="checkbox" sx={{ pl: 1 }}>
                {row.status === 'added' && (
                  <AddCircleOutlineOutlinedIcon sx={{ fontSize: 14, color: 'success.main' }} />
                )}
                {row.status === 'removed' && (
                  <RemoveCircleOutlineOutlinedIcon sx={{ fontSize: 14, color: 'error.main' }} />
                )}
              </TableCell>
              <TableCell>
                <DiffCell oldVal={row.old?.brand} newVal={row.new?.brand} status={row.status} />
              </TableCell>
              <TableCell>
                <DiffCell oldVal={row.old?.code} newVal={row.new?.code} status={row.status} />
              </TableCell>
              <TableCell>
                <DiffCell
                  oldVal={row.old != null ? (row.old.isActive ? 'Yes' : 'No') : undefined}
                  newVal={row.new != null ? (row.new.isActive ? 'Yes' : 'No') : undefined}
                  status={row.status}
                />
              </TableCell>
              <TableCell>
                <DiffCell
                  oldVal={formatDate(row.old?.validFrom)}
                  newVal={formatDate(row.new?.validFrom)}
                  status={row.status}
                />
              </TableCell>
              <TableCell>
                <DiffCell
                  oldVal={formatDate(row.old?.validTo)}
                  newVal={formatDate(row.new?.validTo)}
                  status={row.status}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const RolesDiff: FC<{
  roles: { role: string; status: 'added' | 'removed' | 'unchanged' }[];
}> = ({ roles }) => {
  if (roles.length === 0)
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap">
      {roles.map(({ role, status }) => (
        <Chip
          key={role}
          label={role}
          size="small"
          color={status === 'added' ? 'success' : status === 'removed' ? 'error' : 'default'}
          variant={status === 'unchanged' ? 'outlined' : 'filled'}
          icon={
            status === 'added' ? (
              <AddCircleOutlineOutlinedIcon />
            ) : status === 'removed' ? (
              <RemoveCircleOutlineOutlinedIcon />
            ) : undefined
          }
        />
      ))}
    </Stack>
  );
};

export const EntitlementDiffView: FC<{
  oldSnap: IEmsEntitlementSnapshot | null;
  newSnap: IEmsEntitlementSnapshot | null;
  hideIdentity?: boolean;
}> = ({ oldSnap, newSnap, hideIdentity = false }) => {
  const claimRows = diffClaims(oldSnap?.claims ?? null, newSnap?.claims ?? null);
  const roleDiffs = diffRoles(oldSnap?.roles ?? null, newSnap?.roles ?? null);
  const hasChanges =
    claimRows.some((r) => r.status !== 'unchanged') ||
    roleDiffs.some((r) => r.status !== 'unchanged');

  const process = newSnap?.process ?? oldSnap?.process;
  const system = newSnap?.system ?? oldSnap?.system;
  const sourceId = newSnap?.sourceId ?? oldSnap?.sourceId;
  const srsLink =
    process === 'SUBSCRIPTION' && system && sourceId
      ? `/subscriptions/${system}/${sourceId}`
      : null;

  const identityFields = hideIdentity
    ? []
    : [
        { label: 'Process', old: oldSnap?.process, new: newSnap?.process, mono: false },
        { label: 'System', old: oldSnap?.system, new: newSnap?.system, mono: false },
        { label: 'Source ID', old: oldSnap?.sourceId, new: newSnap?.sourceId, mono: false },
      ];
  const metaFields = [
    { label: 'Imported by', old: oldSnap?.importedBy, new: newSnap?.importedBy, mono: true },
  ];
  const fields = [...identityFields, ...metaFields];

  const idSx = { fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' };

  return (
    <Stack spacing={2}>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
          <Typography variant="caption" color="text.secondary">
            Claims
          </Typography>
          {claimRows.some((r) => r.status === 'added') && (
            <Chip
              label="added"
              size="small"
              color="success"
              variant="outlined"
              sx={{ height: 16, fontSize: '0.65rem' }}
            />
          )}
          {claimRows.some((r) => r.status === 'removed') && (
            <Chip
              label="removed"
              size="small"
              color="error"
              variant="outlined"
              sx={{ height: 16, fontSize: '0.65rem' }}
            />
          )}
          {claimRows.some((r) => r.status === 'modified') && (
            <Chip
              label="modified"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 16, fontSize: '0.65rem' }}
            />
          )}
          {srsLink && (
            <Link
              component={RouterLink}
              to={srsLink}
              variant="caption"
              underline="hover"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, ml: 'auto' }}>
              View subscription
              <OpenInNewOutlinedIcon sx={{ fontSize: 12 }} />
            </Link>
          )}
        </Stack>
        <ClaimsDiffTable rows={claimRows} />
      </Box>

      {!hasChanges && (
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          No claim or role changes detected.
        </Typography>
      )}

      {(roleDiffs.length > 0 || fields.length > 0) && (
        <Stack
          direction="row"
          spacing={2}
          flexWrap="wrap"
          alignItems="center"
          divider={<Divider orientation="vertical" flexItem />}>
          {roleDiffs.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Roles
              </Typography>
              <RolesDiff roles={roleDiffs} />
            </Box>
          )}
          {fields.map(({ label, old: o, new: n, mono }) => {
            const changed = o !== n;
            return (
              <Box key={label}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  {label}
                </Typography>
                {changed ? (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography
                      variant="body2"
                      sx={{ textDecoration: 'line-through', color: 'error.main', ...(mono ? idSx : {}) }}>
                      {o ?? '-'}
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                      →
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'success.main', ...(mono ? idSx : {}) }}>
                      {n ?? '-'}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" sx={mono ? idSx : {}}>
                    {o ?? '-'}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export const EntitlementSnapshotView: FC<{
  snapshot: IEmsEntitlementSnapshot | null;
  hideIdentity?: boolean;
}> = ({ snapshot, hideIdentity = false }) => {
  const idSx = { fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' };

  if (!snapshot)
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
          Claims
        </Typography>
        {snapshot.claims && snapshot.claims.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell>Brand</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Valid from</TableCell>
                  <TableCell>Valid to</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {snapshot.claims.map((claim) => (
                  <TableRow key={`${claim.brand}|${claim.code}`}>
                    <TableCell>{claim.brand ?? '-'}</TableCell>
                    <TableCell>{claim.code ?? '-'}</TableCell>
                    <TableCell>{claim.isActive ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{formatDate(claim.validFrom)}</TableCell>
                    <TableCell>{formatDate(claim.validTo)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No claims
          </Typography>
        )}
      </Box>

      <Stack
        direction="row"
        spacing={2}
        flexWrap="wrap"
        alignItems="center"
        divider={<Divider orientation="vertical" flexItem />}>
        {snapshot.roles && snapshot.roles.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Roles
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {snapshot.roles.map((role) => (
                <Chip key={role} label={role} size="small" variant="outlined" />
              ))}
            </Stack>
          </Box>
        )}
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            Imported by
          </Typography>
          <Typography variant="body2" sx={idSx}>
            {snapshot.importedBy ?? '-'}
          </Typography>
        </Box>
        {!hideIdentity && (
          <>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Process
              </Typography>
              <Typography variant="body2">{snapshot.process ?? '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                System
              </Typography>
              <Typography variant="body2">{snapshot.system ?? '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Source ID
              </Typography>
              <Typography variant="body2">{snapshot.sourceId ?? '-'}</Typography>
            </Box>
          </>
        )}
      </Stack>
    </Stack>
  );
};

// --- Main dialog ---

interface EntitlementEventDetailDialogProps {
  event: IEmsAuditEvent | null;
  onClose: () => void;
}

export const EntitlementEventDetailDialog: FC<EntitlementEventDetailDialogProps> = ({
  event,
  onClose,
}) => {
  if (!event) return null;
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Entitlement event details</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">
                Date
              </Typography>
              <Typography variant="body2">{formatDate(event.dateOccurred)}</Typography>
            </Box>
            {event.correlationId && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Correlation ID
                </Typography>
                <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                  {event.correlationId}
                </Typography>
              </Box>
            )}
          </Stack>

          <Divider />

          {event.type === 'auditing.event.entitlement.upserted.v1' && (
            <EntitlementDiffView
              oldSnap={(event as IEntitlementUpsertedAuditEvent).oldEntitlement}
              newSnap={(event as IEntitlementUpsertedAuditEvent).newEntitlement}
            />
          )}

          {event.type === 'auditing.event.entitlement.deleted.v1' && (
            <EntitlementSnapshotView
              snapshot={(event as IEntitlementDeletedAuditEvent).deletedEntitlement}
            />
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
